/**
 * El set de iconos de UI, con la misma regla que `Icon.astro` de tbit-canvas:
 * línea, `currentColor`, trazo 1.75, 16px por defecto, y un switch CERRADO.
 *
 * El globo de la marca es un SUSTANTIVO, no un marco: envolver cada glifo en un
 * globo los vuelve indistinguibles a 16px (ya pasó una vez, ADR-008 de canvas).
 * Por eso acá el globo aparece UNA vez, como el icono "mensaje", y no alrededor
 * de los demás.
 */
export type IconName =
  | 'bubble'
  | 'inbox'
  | 'doc'
  | 'send'
  | 'sparkle'
  | 'users'
  | 'briefcase'
  | 'search'
  | 'check'
  | 'clock'
  | 'alert'
  | 'mail'
  | 'refresh'
  | 'plus'
  | 'arrow-right'
  | 'mic'
  | 'stop'
  | 'lock'
  | 'bookmark';

const PATHS: Record<IconName, string> = {
  bubble: 'M21 11.5a8.38 8.38 0 0 1-8.5 8.5 8.6 8.6 0 0 1-3.8-.9L3 21l1.9-5.7A8.4 8.4 0 0 1 4 11.5 8.38 8.38 0 0 1 12.5 3 8.38 8.38 0 0 1 21 11.5Z',
  inbox: 'M3 12h5l2 3h4l2-3h5M5 5h14l2 7v6a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1v-6l2-7Z',
  doc: 'M14 3v5h5M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8l-5-5ZM9 13h6M9 17h4',
  send: 'M21 3 3 10.5l7 3 3 7L21 3ZM10 13.5 21 3',
  sparkle: 'M12 3v4M12 17v4M5.6 5.6l2.8 2.8M15.6 15.6l2.8 2.8M3 12h4M17 12h4M5.6 18.4l2.8-2.8M15.6 8.4l2.8-2.8',
  users: 'M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8ZM22 21v-2a4 4 0 0 0-3-3.9M16 3.1a4 4 0 0 1 0 7.8',
  briefcase: 'M4 8h16a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V9a1 1 0 0 1 1-1ZM9 8V5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v3M3 13h18',
  search: 'M11 18a7 7 0 1 0 0-14 7 7 0 0 0 0 14ZM21 21l-5-5',
  check: 'M20 6 9 17l-5-5',
  clock: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18ZM12 7v5l3 2',
  alert: 'M12 8v5M12 17h.01M10.3 3.9 2.4 17a2 2 0 0 0 1.7 3h15.8a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z',
  mail: 'M3 7a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7ZM3 7.5l9 6 9-6',
  refresh: 'M21 9.3V4.5M21 9.3h-4.8M3 14.7v4.8M3 14.7h4.8M4 9.9a8.3 8.3 0 0 1 13.7-3.1L21 9.3M20 14.1a8.3 8.3 0 0 1-13.7 3.1L3 14.7',
  plus: 'M12 5v14M5 12h14',
  'arrow-right': 'M5 12h14M13 6l6 6-6 6',
  // El micrófono es el protagonista del cuestionario hablado: cápsula, arco y
  // pie, sin globo alrededor (la regla del set: el globo es un sustantivo).
  mic: 'M12 15a3 3 0 0 0 3-3V6a3 3 0 0 0-6 0v6a3 3 0 0 0 3 3ZM19 11a7 7 0 0 1-14 0M12 18v3M8.5 21h7',
  stop: 'M8 7h8a1 1 0 0 1 1 1v8a1 1 0 0 1-1 1H8a1 1 0 0 1-1-1V8a1 1 0 0 1 1-1Z',
  lock: 'M7 11V8a5 5 0 0 1 10 0v3M6 11h12a1 1 0 0 1 1 1v8a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1v-8a1 1 0 0 1 1-1Z',
  bookmark: 'M7 4h10a1 1 0 0 1 1 1v16l-6-4-6 4V5a1 1 0 0 1 1-1Z',
};

interface Props {
  name: IconName;
  size?: number;
  className?: string;
  /** Relleno (un estado prendido, como «guardada»). */
  filled?: boolean;
}

export default function Icon({ name, size = 16, className, filled = false }: Props) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill={filled ? 'currentColor' : 'none'}
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      className={className}
      style={{ flex: 'none' }}
    >
      <path d={PATHS[name]} />
    </svg>
  );
}
